import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CalendarEvent, CalendarInfo } from '@elena/shared';
import { api, ApiError } from '../lib/api';
import { Icon } from '../components/Icon';
import { useElena } from '../state';

const TZ = 'Europe/Belgrade';
const belgradeDay = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: TZ });
const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString('sr-Latn-RS', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
  });

export function dayLabel(day: string): string {
  const today = belgradeDay(new Date());
  const tomorrow = belgradeDay(new Date(Date.now() + 86_400_000));
  if (day === today) return 'Danas';
  if (day === tomorrow) return 'Sutra';
  const [y, m, d] = day.split('-').map(Number);
  const text = new Date(Date.UTC(y!, m! - 1, d!, 12)).toLocaleDateString('sr-Latn-RS', {
    timeZone: TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const eventTime = (e: CalendarEvent) =>
  e.allDay ? 'ceo dan' : `${clock(e.start)}–${clock(e.end)}`;

/** Events grouped by Belgrade day, in order. */
export function groupByDay(events: CalendarEvent[]): Array<[string, CalendarEvent[]]> {
  const groups = new Map<string, CalendarEvent[]>();
  for (const e of events) groups.set(e.day, [...(groups.get(e.day) ?? []), e]);
  return [...groups].sort(([a], [b]) => a.localeCompare(b));
}

export function Calendar() {
  const { activity } = useElena();
  const [info, setInfo] = useState<CalendarInfo | null>(null);
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { calendar } = await api.calendar();
      setInfo(calendar);
      if (calendar.configured) {
        const r = await api.calendarEvents(14);
        setEvents(r.events);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kalendar ne odgovara.');
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, activity.length]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Kalendar</h1>
          <p className="muted">
            Isti kalendar kao na iPhone-u. Reci „šta imam sutra“, „ubaci zubara u utorak u 17h i
            podseti me sat ranije“ ili „obriši trening u petak“.
          </p>
        </div>
      </header>

      {info && !info.configured && <ConnectCard info={info} onDone={load} />}

      {info?.configured && (
        <div className="calendar-layout">
          <section className="card" aria-labelledby="agenda-title">
            <header className="card-head">
              <h2 id="agenda-title">Naredne dve nedelje</h2>
              <button
                type="button"
                className="icon-btn ghost"
                aria-label="Osveži"
                onClick={() => void load()}
              >
                <Icon name="refresh" size={16} />
              </button>
            </header>
            {error && <p className="notice">{error}</p>}
            {events && events.length === 0 && !error && (
              <p className="muted">Nemaš ništa u kalendaru narednih 14 dana.</p>
            )}
            <div className="agenda">
              {groupByDay(events ?? []).map(([day, list]) => (
                <div key={day} className="agenda-day">
                  <h3>{dayLabel(day)}</h3>
                  <ul className="list">
                    {list.map((e) => (
                      <li key={e.id} className="list-row agenda-row">
                        <span className="agenda-time mono">{eventTime(e)}</span>
                        <span className="grow">
                          <span className="strong">{e.title}</span>
                          {(e.location || e.recurring) && (
                            <span className="muted small block">
                              {[e.location, e.recurring ? 'ponavlja se' : null]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                        </span>
                        {!e.recurring && (
                          <button
                            type="button"
                            className="icon-btn ghost"
                            aria-label={`Obriši: ${e.title}`}
                            onClick={async () => {
                              if (!window.confirm(`Obrisati „${e.title}“ iz kalendara?`)) return;
                              try {
                                await api.removeEvent(e.href);
                              } catch (err) {
                                setError(err instanceof ApiError ? err.message : 'Nije obrisano.');
                              }
                              await load();
                            }}
                          >
                            <Icon name="trash" size={16} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <div className="stack-18">
            <AddEventCard onAdded={load} />
            <AccountCard info={info} onChange={load} />
          </div>
        </div>
      )}
    </div>
  );
}

function AddEventCard({ onAdded }: { onAdded: () => Promise<void> }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(() => belgradeDay(new Date()));
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [alert, setAlert] = useState(-1);
  const [location, setLocation] = useState('');
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const r = await api.addEvent({
        title: title.trim(),
        start: time ? `${date}T${time}` : date,
        durationMinutes: time ? duration : 1440,
        ...(location.trim() ? { location: location.trim() } : {}),
        ...(alert >= 0 ? { alertMinutes: alert } : {}),
      });
      setNote({ ok: true, text: r.message });
      setTitle('');
      setLocation('');
      await onAdded();
    } catch (err) {
      setNote({ ok: false, text: err instanceof ApiError ? err.message : 'Nije dodato.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="add-ev-title">
      <h2 id="add-ev-title">Novi događaj</h2>
      <form className="stack-12" onSubmit={submit}>
        <label className="field-label">
          Naziv
          <input
            className="field"
            value={title}
            maxLength={120}
            required
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Zubar"
          />
        </label>
        <div className="form-2">
          <label className="field-label">
            Datum
            <input
              className="field"
              type="date"
              value={date}
              required
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className="field-label">
            Vreme
            <input
              className="field"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </label>
        </div>
        <div className="form-2">
          <label className="field-label">
            Trajanje
            <select
              className="field"
              value={duration}
              disabled={!time}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {[15, 30, 45, 60, 90, 120, 180].map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? `${m} min` : `${m / 60} h`}
                </option>
              ))}
            </select>
          </label>
          <label className="field-label">
            Podseti me
            <select
              className="field"
              value={alert}
              onChange={(e) => setAlert(Number(e.target.value))}
            >
              <option value={-1}>Ne</option>
              <option value={0}>Kad počne</option>
              <option value={15}>15 min pre</option>
              <option value={30}>30 min pre</option>
              <option value={60}>1 h pre</option>
              <option value={120}>2 h pre</option>
              <option value={1440}>Dan pre</option>
            </select>
          </label>
        </div>
        <label className="field-label">
          Mesto (nije obavezno)
          <input
            className="field"
            value={location}
            maxLength={120}
            onChange={(e) => setLocation(e.target.value)}
          />
        </label>
        <p className="muted small">Bez vremena je događaj za ceo dan.</p>
        <button type="submit" className="btn primary" disabled={busy || !title.trim()}>
          {busy ? 'Dodajem…' : 'Dodaj u kalendar'}
        </button>
        {note && (
          <p className={note.ok ? 'notice ok' : 'notice'} role="status">
            {note.text}
          </p>
        )}
      </form>
    </section>
  );
}

function AccountCard({ info, onChange }: { info: CalendarInfo; onChange: () => Promise<void> }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="card" aria-labelledby="cal-acc-title">
      <header className="card-head">
        <h2 id="cal-acc-title">Nalog</h2>
        <span className="pill ok">
          <span className="dot on" /> Povezan
        </span>
      </header>
      <p className="muted small break">{info.username}</p>
      <label className="field-label">
        Nove događaje upisuj u
        <select
          className="field"
          value={info.writeTo}
          onChange={async (e) => {
            setError(null);
            try {
              await api.connectCalendar({
                url: info.url,
                username: info.username,
                writeTo: e.target.value,
              });
              await onChange();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Nije sačuvano.');
            }
          }}
        >
          {info.calendars.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <p className="muted small">Čitam iz svih: {info.calendars.map((c) => c.name).join(', ')}.</p>
      {error && <p className="notice">{error}</p>}
      <button
        type="button"
        className="btn danger self-start"
        onClick={async () => {
          await api.disconnectCalendar().catch(() => undefined);
          await onChange();
        }}
      >
        Prekini vezu
      </button>
    </section>
  );
}

function ConnectCard({ info, onDone }: { info: CalendarInfo; onDone: () => Promise<void> }) {
  const [username, setUsername] = useState(info.username);
  const [password, setPassword] = useState('');
  const [url, setUrl] = useState(info.url || 'https://caldav.icloud.com');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.connectCalendar({
        url: url.trim(),
        username: username.trim(),
        password: password.trim(),
      });
      setPassword('');
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Povezivanje nije uspelo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="calendar-layout">
      <section className="card" aria-labelledby="cal-how">
        <h2 id="cal-how">Kako da povežeš iPhone kalendar</h2>
        <ol className="howto">
          <li>
            Otvori <span className="strong">account.apple.com</span> i prijavi se.
          </li>
          <li>
            <span className="strong">Prijava i bezbednost → Lozinke za aplikacije</span> → dodaj
            novu, nazovi je „Elena“.
          </li>
          <li>Kopiraj lozinku koju Apple pokaže (oblik xxxx-xxxx-xxxx-xxxx).</li>
          <li>Ovde upiši svoj Apple ID (email) i tu lozinku, pa klikni Poveži.</li>
          <li>
            Na iPhone-u:{' '}
            <span className="strong">Podešavanja → [tvoje ime] → iCloud → Kalendar</span> mora biti
            uključen.
          </li>
        </ol>
        <p className="muted small">
          Lozinka za aplikaciju važi samo za kalendar i možeš je opozvati bilo kad. Tvoja prava
          Apple lozinka ostaje tajna. Radi i sa Fastmail, Nextcloud i drugim CalDAV kalendarima.
        </p>
      </section>

      <section className="card" aria-labelledby="cal-connect">
        <h2 id="cal-connect">Poveži kalendar</h2>
        <form className="stack-12" onSubmit={submit}>
          <label className="field-label">
            Apple ID (email)
            <input
              className="field"
              type="email"
              autoComplete="username"
              value={username}
              required
              onChange={(e) => setUsername(e.target.value)}
            />
          </label>
          <label className="field-label">
            Lozinka za aplikaciju
            <input
              className="field"
              type="password"
              autoComplete="new-password"
              value={password}
              required
              placeholder="xxxx-xxxx-xxxx-xxxx"
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <details>
            <summary className="muted small">Drugi CalDAV server</summary>
            <label className="field-label">
              Adresa servera
              <input
                className="field"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </label>
          </details>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Povezujem…' : 'Poveži'}
          </button>
          {error && <p className="notice">{error}</p>}
        </form>
      </section>
    </div>
  );
}
